// ============================================================================
// WhatsApp Status Indicator — Indicador de estado en el header
// ============================================================================
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { whatsappApi } from '../../api/whatsapp';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_COLOR = {
  CONNECTED:    { dot: 'bg-green-500',  label: 'WhatsApp conectado' },
  QR_PENDING:   { dot: 'bg-amber-400',  label: 'Esperando escaneo QR' },
  CONNECTING:   { dot: 'bg-amber-400',  label: 'Sincronizando...' },
  DISCONNECTED: { dot: 'bg-red-500',    label: 'WhatsApp desconectado' },
  FAILED:       { dot: 'bg-red-500',    label: 'Error de conexión' },
} as const;

export function WhatsAppStatusIndicator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<string>('DISCONNECTED');
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Solo visible para GESTOR y ADMIN
  if (!user || (user.role !== 'GESTOR_SEDE' && user.role !== 'ADMIN')) return null;

  const poll = async () => {
    try {
      const data = await whatsappApi.getStatus();
      setStatus(data.status);
      setPhoneNumber(data.phoneNumber);
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowTooltip(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cfg = STATUS_COLOR[status as keyof typeof STATUS_COLOR] || STATUS_COLOR.DISCONNECTED;
  const isConnected = status === 'CONNECTED';

  return (
    <div className="relative" ref={tooltipRef}>
      <button
        onClick={() => navigate('/admin/whatsapp')}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="flex items-center gap-1.5 p-2 rounded-lg hover:bg-slate-50 transition-colors"
        title="Estado de WhatsApp"
      >
        {/* Icono WhatsApp */}
        <span className="material-symbols-outlined text-slate-400 text-xl">chat</span>
        {/* Punto de estado */}
        <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${status === 'CONNECTING' || status === 'QR_PENDING' ? 'animate-pulse' : ''}`} />
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900 text-white text-xs rounded-xl p-3 z-50 shadow-xl">
          <p className="font-semibold mb-0.5">{cfg.label}</p>
          {isConnected && phoneNumber && (
            <p className="text-gray-300">+{phoneNumber}</p>
          )}
          {!isConnected && (
            <p className="text-gray-400 mt-1">Pulsa para gestionar</p>
          )}
          {/* Flecha del tooltip */}
          <div className="absolute -top-1.5 right-4 w-3 h-3 bg-gray-900 rotate-45 rounded-sm" />
        </div>
      )}
    </div>
  );
}

export default WhatsAppStatusIndicator;
